import { useState } from "react";
import { TbInfoCircle } from "react-icons/tb";
import {
  Alert,
  Anchor,
  AppShell,
  Badge,
  Box,
  Container,
  Group,
  SegmentedControl,
  Stack,
  Text,
  Title,
  rem,
  useComputedColorScheme,
} from "@mantine/core";

import { ColorSchemeActionIcon } from "~/components/color-scheme-action-icon.component";
import { ApiKeyForm } from "~/components/api-key-form";
import { NumberLookupForm } from "~/components/number-lookup-form";
import { ImsiLookupForm } from "~/components/imsi-lookup-form";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Application
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const apiKey = new URL(location.href).searchParams.get("key");

export function Application() {
  const computedColorScheme = useComputedColorScheme();

  const [activeSegment, setActiveSegment] = useState("number-lookup");

  return (
    <AppShell>
      <AppShell.Main>
        <Stack h="100dvh">
          <Group justify="flex-end" p="md">
            <ColorSchemeActionIcon
              tooltipProps={{ position: "left" }}
              menuProps={{ position: "bottom-end" }}
            />
          </Group>

          <Container size="md" w="100%" py={rem(40)}>
            <Stack gap={rem(80)}>
              <Stack gap="xs" align="center">
                <Title order={1}>
                  Number<span style={{ fontWeight: "normal" }}>Lookup</span>
                </Title>

                <Group gap="xs">
                  <img
                    src="/logo.svg"
                    alt="Datafon d.o.o."
                    width={14}
                    height={14}
                  />

                  {computedColorScheme === "dark" ? (
                    <img src="/logo-text-dark.svg" height={14} />
                  ) : (
                    <img src="/logo-text-light.svg" height={14} />
                  )}
                </Group>
              </Stack>

              {!apiKey ? (
                <Stack gap="xl" align="center" w="100%">
                  <Box w="100%" miw={rem(380)} maw={rem(480)}>
                    <Stack gap="xl">
                      <Alert
                        icon={<TbInfoCircle />}
                        title="Welcome to Datafon Number Lookup service"
                      >
                        Please enter your API key to start using the service.
                      </Alert>

                      <ApiKeyForm />
                    </Stack>
                  </Box>
                </Stack>
              ) : (
                <Stack gap="xl" align="center" w="100%">
                  <Box w="100%" miw={rem(380)} maw={rem(480)}>
                    <SegmentedControl
                      w="100%"
                      value={activeSegment}
                      data={[
                        { value: "number-lookup", label: "Number Lookup" },
                        { value: "imsi-lookup", label: "IMSI Lookup" },
                      ]}
                      onChange={(value) => setActiveSegment(value)}
                    />
                  </Box>

                  {activeSegment === "number-lookup" && (
                    <NumberLookupForm apiKey={apiKey} />
                  )}

                  {activeSegment === "imsi-lookup" && (
                    <ImsiLookupForm apiKey={apiKey} />
                  )}
                </Stack>
              )}
            </Stack>
          </Container>

          <Group justify="center" p="md" mt="auto">
            <Stack align="center" gap="xs">
              <Badge variant="default" size="xs" tt="none" c="dimmed">
                Current Version: v{__APP_VERSION__}
              </Badge>

              <Stack align="center" gap={0}>
                <Text fz="xs" ta="center" c="dimmed">
                  Made with ❤️ in Pula by{" "}
                  <Anchor fz="xs" href="https://datafon.net" target="_blank">
                    Datafon
                  </Anchor>
                </Text>

                <Text fz="xs" ta="center" c="dimmed">
                  &copy; {new Date().getFullYear()}{" "}
                  <Anchor fz="xs" href="https://datafon.net" target="_blank">
                    Datafon d.o.o.
                  </Anchor>{" "}
                  All rights reserved.
                </Text>
              </Stack>
            </Stack>
          </Group>
        </Stack>
      </AppShell.Main>
    </AppShell>
  );
}
